import { RowActions } from "@/shared/ui/RowActions";
import { DataTable, type TanstackReactTable } from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import type { MemberTierDTO } from "../api/client";

type MemberTiersTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: MemberTierDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onEdit: (row: MemberTierDTO) => void;
  onDelete: (id: string) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

export function MemberTiersTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onEdit,
  onDelete,
  onPaginationChange,
  onSortingChange,
}: MemberTiersTableProps) {
  const columns: TanstackReactTable.ColumnDef<MemberTierDTO>[] = [
    {
      id: "nameLo",
      header: "ຊື່ (ລາວ)",
      cell: ({ row }) => {
        const n = row.original.name as { lo?: string };
        return n?.lo ?? "—";
      },
    },
    {
      id: "nameTh",
      header: "ຊື່ (ໄທ)",
      cell: ({ row }) => {
        const n = row.original.name as { th?: string };
        return (
          <span className="text-muted-foreground text-sm">{n?.th ?? "—"}</span>
        );
      },
    },
    {
      id: "nameEn",
      header: "ຊື່ (ອັງກິດ)",
      cell: ({ row }) => {
        const n = row.original.name as { en?: string };
        return (
          <span className="text-muted-foreground text-sm">{n?.en ?? "—"}</span>
        );
      },
    },
    {
      id: "sortOrder",
      header: "ລຳດັບ",
      cell: ({ row }) => row.original.sortOrder,
    },
    {
      id: "customerCount",
      header: "ຈຳນວນລູກຄ້າ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.customerCount ?? 0}
        </span>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        if (!canManage) return null;
        const actions = [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          },
          {
            label: "ລຶບ",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: () => onDelete(row.original.id),
            variant: "destructive" as const,
          },
        ];
        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<MemberTierDTO, unknown>
      noDataMessage="ບໍ່ພົບລະດັບສະມາຊິກ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(s) => {
        if (s[0]?.id === "") return;
        onSortingChange(s[0]?.id || "", s[0]?.desc || false);
      }}
    />
  );
}
